import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { tasksAPI } from '../services/api';
import FileUpload from './FileUpload';
import {
  TASK_TYPE_GROUP_ORDER,
  getDefaultsForTaskType,
  getTaskType,
  getTaskTypesGrouped,
} from '../data/taskTypes';

const EMPTY_FORM = {
  title: '',
  description: '',
  task_type: '',
  location: '',
  ideal_outcome: '',
  status: 'pending',
  priority: 'medium',
  category: '',
  allocated_to: '',
};

const TaskForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [attachments, setAttachments] = useState([]);
  const [existingAttachments, setExistingAttachments] = useState([]);
  const [approvedUsers, setApprovedUsers] = useState([]);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [autoFilledFromType, setAutoFilledFromType] = useState(null);

  const groupedTypes = getTaskTypesGrouped();
  const selectedType = getTaskType(formData.task_type);

  const loadTask = useCallback(async () => {
    try {
      const response = await tasksAPI.getTask(id);
      setFormData({
        ...EMPTY_FORM,
        ...response.data,
        allocated_to: response.data.allocated_to || '',
      });
      setAutoFilledFromType(null);

      try {
        const attResponse = await tasksAPI.getAttachments(id);
        setExistingAttachments(attResponse.data || []);
      } catch (err) {
        console.error('Error loading attachments:', err);
      }
    } catch (err) {
      setError('Failed to load task');
    }
  }, [id]);

  const loadApprovedUsers = useCallback(async () => {
    try {
      const response = await tasksAPI.getApprovedUsers();
      setApprovedUsers(response.data || []);
    } catch (err) {
      console.error('Error loading approved users:', err);
    }
  }, []);

  useEffect(() => {
    loadApprovedUsers();
    if (isEdit) {
      loadTask();
    }
  }, [isEdit, id, loadTask, loadApprovedUsers]);

  const applyTypeDefaults = (typeId, { force = false } = {}) => {
    const defaults = getDefaultsForTaskType(typeId);
    if (!defaults) {
      setFormData((prev) => ({ ...prev, task_type: typeId }));
      setAutoFilledFromType(null);
      return;
    }

    setFormData((prev) => {
      const next = { ...prev, task_type: typeId };
      const fields = ['title', 'description', 'location', 'ideal_outcome', 'category', 'priority'];
      for (const field of fields) {
        const wasAuto = autoFilledFromType && prev[field] === getDefaultsForTaskType(autoFilledFromType)?.[field];
        if (force || !prev[field] || wasAuto) {
          next[field] = defaults[field];
        }
      }
      return next;
    });
    setAutoFilledFromType(typeId);
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    if (!typeId) {
      setFormData((prev) => ({ ...prev, task_type: '' }));
      setAutoFilledFromType(null);
      return;
    }
    // On create always fill; on edit fill empty or previously auto-filled fields,
    // and force-fill when user picks a type on a new task.
    applyTypeDefaults(typeId, { force: !isEdit });
  };

  const handleChange = (e) => {
    const value =
      e.target.type === 'number'
        ? e.target.value === ''
          ? ''
          : parseInt(e.target.value, 10) || 0
        : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value,
    });
  };

  const handleFilesChange = (files) => {
    setAttachments(files);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const taskData = {
        ...formData,
        attachments: attachments,
      };

      if (isEdit) {
        await tasksAPI.updateTask(id, taskData);
      } else {
        await tasksAPI.createTask(taskData);
      }

      navigate('/tasks');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save task');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="main-content">
      <div className="page-header">
        <h1 className="page-title">{isEdit ? 'Edit Task' : 'Create New Task'}</h1>
        <p className="page-subtitle">
          {isEdit
            ? 'Update task details'
            : 'Choose a task type to auto-fill title, description, and defaults from the estate manual'}
        </p>
      </div>

      <div className="card">
        {error && <div className="alert alert-error">{error}</div>}
        <form onSubmit={handleSubmit} style={styles.form}>
          <div className="form-group">
            <label className="form-label">Task Type *</label>
            <select
              name="task_type"
              value={formData.task_type}
              onChange={handleTypeChange}
              required
              className="form-select"
            >
              <option value="">Select task type</option>
              {TASK_TYPE_GROUP_ORDER.map((group) => {
                const items = groupedTypes[group] || [];
                if (!items.length) return null;
                return (
                  <optgroup key={group} label={group}>
                    {items.map((item) => (
                      <option key={item.id} value={item.id}>
                        {item.label}
                      </option>
                    ))}
                  </optgroup>
                );
              })}
            </select>
            {selectedType?.docsUrl && (
              <small className="form-help">
                Manual reference:{' '}
                <a href={selectedType.docsUrl} target="_blank" rel="noopener noreferrer">
                  {selectedType.docsUrl.replace('https://docs.lakebennettestate.com', '')}
                </a>
              </small>
            )}
            {isEdit && formData.task_type && (
              <button
                type="button"
                className="btn btn-secondary"
                style={styles.refillBtn}
                onClick={() => applyTypeDefaults(formData.task_type, { force: true })}
              >
                Re-apply type defaults
              </button>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              name="title"
              value={formData.title}
              onChange={handleChange}
              required
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              rows={6}
              className="form-textarea"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Location</label>
            <input
              type="text"
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Where the task is to be conducted"
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Ideal Outcome</label>
            <textarea
              name="ideal_outcome"
              value={formData.ideal_outcome}
              onChange={handleChange}
              rows={3}
              placeholder="Describe the ideal outcome for this task"
              className="form-textarea"
            />
          </div>

          <div style={styles.row}>
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleChange}
                className="form-select"
              >
                <option value="pending">Pending</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Priority</label>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className="form-select"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Category</label>
            <input
              type="text"
              name="category"
              value={formData.category}
              onChange={handleChange}
              className="form-input"
            />
          </div>

          <div className="form-group">
            <label className="form-label">Allocate To</label>
            <select
              name="allocated_to"
              value={formData.allocated_to}
              onChange={handleChange}
              className="form-select"
            >
              <option value="">Default (Administrator)</option>
              {approvedUsers.map((user) => (
                <option key={user.id} value={user.id}>
                  {user.username} {user.is_admin ? '(Admin)' : ''}
                </option>
              ))}
            </select>
            <small className="form-help">
              Select a user to allocate this task to. If not specified, it will be allocated to the
              administrator by default.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Attachments</label>
            <FileUpload
              onFilesChange={handleFilesChange}
              existingFiles={existingAttachments}
            />
          </div>

          <div style={styles.buttonGroup}>
            <button type="submit" disabled={loading} className="btn btn-primary">
              {loading ? 'Saving...' : isEdit ? 'Update Task' : 'Create Task'}
            </button>
            <button
              type="button"
              onClick={() => navigate('/tasks')}
              className="btn btn-secondary"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const styles = {
  form: {
    display: 'flex',
    flexDirection: 'column',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '1rem',
  },
  buttonGroup: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1.5rem',
    paddingTop: '1.5rem',
    borderTop: '1px solid #e5e7eb',
  },
  refillBtn: {
    marginTop: '0.75rem',
  },
};

export default TaskForm;
